import type {
  GameConfiguration,
  TenantDefaultConfigurations,
} from '@sirius/contracts/admin'
import { useQuery } from '@tanstack/react-query'
import { useAdminClient } from '~/api/adminClientContext'
import { FORM_RECORD_QUERY_OPTIONS } from '~/lib/formRecordQuery'
import { USE_DEFAULT, effective, fromOwn } from '~/lib/inheritable'

// A tenant's default configuration of each game (`tenants.defaultConfigurations`):
// what its cabinets on Use default boot. Set in Settings › Configurations;
// the cabinet form shows it as a new cabinet's Use default.

export const defaultConfigurationsQueryKey = (tenantId: string) => [
  'tenantDefaultConfigurations',
  tenantId,
]

/**
 * The tenant's default configurations (`enabled`: only for staff and its
 * owners/admins, the API answers FORBIDDEN to members). Loaded fresh on
 * mount, never refetched in the background: the Configurations tab resets
 * its form to every new answer, which would drop unsaved picks.
 */
export function useTenantDefaultConfigurations(
  tenantId: string,
  enabled = true,
) {
  const client = useAdminClient()
  return useQuery({
    queryKey: defaultConfigurationsQueryKey(tenantId),
    queryFn: () => client.tenants.defaultConfigurations.get({ tenantId }),
    enabled,
    ...FORM_RECORD_QUERY_OPTIONS,
  })
}

/**
 * What a cabinet of the tenant without a configuration of its own boots, per
 * game: the tenant's default, else the game's (null while neither has a
 * current version).
 */
export function cabinetDefaults(
  defaults: TenantDefaultConfigurations | undefined,
): ReadonlyMap<string, string | null> {
  return new Map(
    (defaults?.games ?? []).map(
      ({ gameId, configurationId, inheritedConfigurationId }) => [
        gameId,
        effective(configurationId, inheritedConfigurationId),
      ],
    ),
  )
}

/**
 * The form's value per game: each game's select value (`USE_DEFAULT` for
 * null), keyed by game id.
 */
export function choicesOf(
  defaults: TenantDefaultConfigurations,
): Record<string, string> {
  return Object.fromEntries(
    defaults.games.map(({ gameId, configurationId }) => [
      gameId,
      fromOwn(configurationId),
    ]),
  )
}

/**
 * The update's games: every game whose choice differs from its saved
 * default, null for Use default.
 */
export function changedDefaults(
  defaults: TenantDefaultConfigurations,
  choices: Readonly<Record<string, string>>,
) {
  return defaults.games.flatMap(({ gameId, configurationId }) => {
    const choice = choices[gameId] ?? fromOwn(configurationId)
    const next = choice === USE_DEFAULT ? null : choice
    return next === configurationId ? [] : [{ gameId, configurationId: next }]
  })
}

/**
 * The name of the configuration `id` among a game's configurations: undefined
 * while they load or when it is not among them, null for none.
 */
export function configurationName(
  configurations: readonly GameConfiguration[] | undefined,
  id: string | null | undefined,
): string | null | undefined {
  if (id === null) {
    return null
  }
  if (id === undefined || configurations === undefined) {
    return undefined
  }
  return configurations.find((configuration) => configuration.id === id)?.name
}
